import { useCallback, useSyncExternalStore } from "react";

type Theme = "light" | "dark";

const listeners = new Set<() => void>();
let currentTheme: Theme = "light";
let initialized = false;

const readPreferredTheme = (): Theme => {
  if (typeof window === "undefined") {
    return "light";
  }
  const storedTheme = window.localStorage.getItem("theme");
  if (storedTheme === "light" || storedTheme === "dark") {
    return storedTheme;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const applyThemeToDom = (theme: Theme) => {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.classList.toggle("dark", theme === "dark");
};

const updateTheme = (nextTheme: Theme) => {
  if (currentTheme === nextTheme) {
    return;
  }
  currentTheme = nextTheme;
  if (typeof window !== "undefined") {
    window.localStorage.setItem("theme", nextTheme);
  }
  applyThemeToDom(nextTheme);
  listeners.forEach((listener) => listener());
};

const ensureInitialized = () => {
  if (initialized) {
    return;
  }
  if (typeof window === "undefined") {
    return;
  }
  initialized = true;
  currentTheme = readPreferredTheme();
  applyThemeToDom(currentTheme);
};

const subscribe = (listener: () => void) => {
  ensureInitialized();
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => {
  ensureInitialized();
  return currentTheme;
};

const getServerSnapshot = () => "light";

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const toggleTheme = useCallback(() => {
    const nextTheme: Theme = theme === "light" ? "dark" : "light";
    updateTheme(nextTheme);
  }, [theme]);

  return { theme, toggleTheme };
}
